import React, { useEffect, useMemo, useState } from 'react';
import { getMyProfile, getTemplateAccess, saveTemplate } from '../Api/Profileapi';
import TemplatePreview from '../Components/TemplatePreview';
import WorkflowProgress from '../Components/WorkflowProgress';
import {
  CATEGORY_TABS,
  LIBRARY_SIZE,
  templateTier,
  isTemplateLocked,
  templatesFor,
  getTemplate,
  categoryLabel,
} from '../Data/templates';
import '../Style/Templates.css';

const PAGE = 12;
function LockIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="13"
      height="13"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="4" y="11" width="16" height="10" rx="2" />
      <path d="M8 11V8a4 4 0 0 1 8 0v3" />
    </svg>
  );
}

export default function Templates({ onBack, onContinue, onUpgrade }) {
  const [profile, setProfile] = useState(null);
  const [limit, setLimit] = useState(0);
  const [planTier, setPlanTier] = useState('starter');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [category, setCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [shown, setShown] = useState(PAGE);
  const [selectedId, setSelectedId] = useState('');
  const [openId, setOpenId] = useState('');
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState('');

  useEffect(() => {
    let off = false;
    (async () => {
      try {
        const [{ profile: p }, access] = await Promise.all([getMyProfile(), getTemplateAccess()]);
        if (off) return;
        setProfile(p);
        setLimit(access.templates_per_category);
        setPlanTier(templateTier(access.plan_type, access.status));
        if (p) {
          setSelectedId(p.template_id || '');
          // start on the category the user chose in the wizard
          if (p.category && CATEGORY_TABS.some((t) => t.key === p.category)) {
            setCategory(p.category);
          }
        }
      } catch (err) {
        if (!off) setError(err.message);
      } finally {
        if (!off) setLoading(false);
      }
    })();
    return () => {
      off = true;
    };
  }, []);

  const isLocked = (template) => isTemplateLocked(template, planTier, limit);
  const list = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    const templates = templatesFor(category, planTier);
    return query
      ? templates.filter((template) =>
          `${template.name} ${template.styleName} ${categoryLabel(template.category)}`
            .toLowerCase()
            .includes(query)
        )
      : templates;
  }, [category, planTier, searchQuery]);
  const availableList = useMemo(
    () => list.filter((template) => !isTemplateLocked(template, planTier, limit)),
    [list, planTier, limit]
  );
  const visible = availableList.slice(0, shown);
  const openT = openId ? getTemplate(openId) : null;
  const selectedT = selectedId ? getTemplate(selectedId) : null;
  const selectedLocked = selectedT ? isLocked(selectedT) : false;

  function pickCategory(key) {
    setCategory(key);
    setShown(PAGE);
  }

  function searchTemplates(value) {
    setSearchQuery(value);
    setShown(PAGE);
  }

  function closeModal() {
    setOpenId('');
    setModalError('');
  }

  // Esc closes the preview, and the page behind it does not scroll.
  useEffect(() => {
    if (!openId) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') closeModal();
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [openId]);

  async function applyTemplate(t) {
    setSaving(true);
    setModalError('');
    try {
      await saveTemplate(t.id);
      setSelectedId(t.id);
      closeModal();
    } catch (err) {
      setModalError(err.message);
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <section className="tpls">
        <div className="tpls__inner">
          <WorkflowProgress activeStep={0} />
          <p className="tpls__loading">Loading templates…</p>
        </div>
      </section>
    );
  }

  if (!profile) {
    return (
      <section className="tpls">
        <div className="tpls__inner">
          <WorkflowProgress activeStep={0} />
          <div className="tpls__empty">
            <h2 className="tpls__title">Create your profile first</h2>
            <p className="tpls__lead">
              Templates use your saved details. Open Profile from the dashboard and finish the steps.
            </p>
            {error && <p className="tpls__error">{error}</p>}
            <button type="button" className="tpls__btn" onClick={onBack}>
              Back to dashboard
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="tpls">
      <div className="tpls__inner">
        <div className="tpls__topbar">
          <button type="button" className="tpls__back" onClick={onBack}>
            &larr; Dashboard
          </button>
          <span className="tpls__brand">MLink</span>
        </div>

        <WorkflowProgress activeStep={0} />

        <div className="tpls__head">
          <h1 className="tpls__title">Choose your template</h1>
          <p className="tpls__lead">
            Every design below already shows your own details. Pick the look you like.
            {limit < 25 ? (
              <>
                {' '}
                Your plan includes {limit} templates per category.{' '}
                <button type="button" className="tpls__link" onClick={onUpgrade}>
                  Upgrade for more templates
                </button>
              </>
            ) : limit < LIBRARY_SIZE ? (
              <> Your plan includes 25 templates per category.</>
            ) : (
              <> Your trial includes all {LIBRARY_SIZE} templates per category.</>
            )}
          </p>
        </div>

        {error && <p className="tpls__error">{error}</p>}

        <div className="tpls__layout">
          <aside className="tpls__side">
            <p className="tpls__side-title">Browse by category</p>
            <div className="tpls__cats">
              {CATEGORY_TABS.map((c) => (
                <button
                  type="button"
                  key={c.key}
                  className={`tpls__cat${category === c.key ? ' tpls__cat--on' : ''}`}
                  aria-pressed={category === c.key}
                  onClick={() => pickCategory(c.key)}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </aside>

          <div className="tpls__results">
            <div className="tpls__tools">
              <div className="tpls__search">
                <svg viewBox="0 0 24 24" width="19" height="19" fill="none" aria-hidden="true">
                  <circle cx="10.8" cy="10.8" r="6.8" />
                  <path d="m16 16 4.2 4.2" />
                </svg>
                <label className="tpls__sr-only" htmlFor="template-search">
                  Search templates
                </label>
                <input
                  id="template-search"
                  type="search"
                  value={searchQuery}
                  onChange={(event) => searchTemplates(event.target.value)}
                  placeholder="Search by style or category"
                />
                {searchQuery && (
                  <button
                    type="button"
                    className="tpls__search-clear"
                    aria-label="Clear search"
                    onClick={() => searchTemplates('')}
                  >
                    &times;
                  </button>
                )}
              </div>
              <p className="tpls__result-count" aria-live="polite">
                {availableList.length} {availableList.length === 1 ? 'design' : 'designs'}
                {searchQuery.trim() ? ' found' : ' available'}
              </p>
            </div>
            <div className="tpls__grid">
              {visible.map((t) => {
                const locked = isLocked(t);
                const selected = selectedId === t.id;
                return (
                  <article
                    key={t.id}
                    className={`tpls__card${locked ? ' tpls__card--locked' : ''}${
                      selected ? ' tpls__card--sel' : ''
                    }`}
                  >
                    <div className="tpls__thumb">
                      {/* No customization here on purpose — the picker shows each
                          template's own look so categories/variants stay distinct.
                          Customization only applies once a template is selected. */}
                      <TemplatePreview template={t} profile={profile} decorative />
                      {locked && (
                        <span className="tpls__badge tpls__badge--lock">
                          <LockIcon /> Locked
                        </span>
                      )}
                      {selected && <span className="tpls__badge tpls__badge--sel">Selected</span>}
                    </div>
                    <div className="tpls__meta">
                      <span className="tpls__name">{t.name}</span>
                      <span className="tpls__style">{t.styleName}</span>
                    </div>
                    <button
                      type="button"
                      className="tpls__hit"
                      aria-label={`Preview ${t.name}${locked ? ' (locked)' : ''}`}
                      onClick={() => setOpenId(t.id)}
                    />
                  </article>
                );
              })}
            </div>

            {availableList.length === 0 && (
              <div className="tpls__no-results">
                <span aria-hidden="true">⌕</span>
                <h2>No matching designs</h2>
                <p>Try a different style or category name.</p>
                <button type="button" className="tpls__btn tpls__btn--ghost" onClick={() => searchTemplates('')}>
                  Clear search
                </button>
              </div>
            )}

            {shown < availableList.length && (
              <div className="tpls__more">
                <button
                  type="button"
                  className="tpls__btn tpls__btn--ghost"
                  onClick={() => setShown((n) => n + PAGE)}
                >
                  Show more templates ({availableList.length - shown} left)
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {selectedT && (
        <div className="tpls__bar" role="status">
          <span>
            {selectedLocked ? (
              <>
                <strong>{selectedT.name}</strong> isn&rsquo;t included in your current plan. Choose
                an included design or upgrade.
              </>
            ) : (
              <>
                Selected: <strong>{selectedT.name}</strong> &middot; {selectedT.styleName}
              </>
            )}
          </span>
          <button
            type="button"
            className="tpls__bar-btn"
            disabled={selectedLocked}
            onClick={() => onContinue && onContinue(selectedT.id)}
          >
            {selectedLocked ? 'Plan locked' : 'Continue'}
          </button>
        </div>
      )}

      {openT && (
        <div className="tpls__overlay" onClick={closeModal}>
          <div
            className="tpls__modal"
            role="dialog"
            aria-modal="true"
            aria-label={`${openT.name} preview`}
            onClick={(e) => e.stopPropagation()}
          >
            <button type="button" className="tpls__x" aria-label="Close preview" onClick={closeModal}>
              &times;
            </button>

            <div className="tpls__modal-stage">
              <div className="tpls__modal-phone">
                {/* Same reasoning as the grid: the preview modal shows the
                    template as-is, not whatever the user previously
                    customized on a different template. */}
                <TemplatePreview template={openT} profile={profile} />
              </div>
            </div>

            <div className="tpls__modal-info">
              <p className="tpls__eyebrow">{categoryLabel(openT.category)}</p>
              <h2 className="tpls__modal-title">{openT.name}</h2>
              <p className="tpls__modal-style">{openT.styleName} style</p>
              <p className="tpls__modal-text">
                This is your own profile inside the template. Your photo, bio, contact details and
                social links come from what you already saved.
              </p>

              {isLocked(openT) && (
                <p className="tpls__note">
                  <LockIcon /> This design is not included in your current plan.
                </p>
              )}
              {modalError && <p className="tpls__error">{modalError}</p>}

              <div className="tpls__modal-actions">
                {isLocked(openT) ? (
                  <button type="button" className="tpls__btn" onClick={onUpgrade}>
                    Upgrade to unlock
                  </button>
                ) : (
                  <button
                    type="button"
                    className="tpls__btn"
                    disabled={saving || selectedId === openT.id}
                    onClick={() => applyTemplate(openT)}
                  >
                    {selectedId === openT.id ? 'Selected' : saving ? 'Saving…' : 'Use this template'}
                  </button>
                )}
                <button type="button" className="tpls__btn tpls__btn--ghost" onClick={closeModal}>
                  Keep browsing
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}